"use strict";
Object.defineProperty(exports, Symbol.toStringTag, { value: "Module" });
const React = require("react");
const events = require("../../../../constants/constants/events.js");
const utils = require("./utils.js");
function writeToStorage(key, value) {
  if (!utils.isBrowser()) {
    return;
  }
  try {
    globalThis.localStorage.setItem(key, JSON.stringify(value));
    globalThis.dispatchEvent(
      new CustomEvent(events.LOCAL_STORAGE_CHANGE_EVENT, {
        detail: { key }
      })
    );
  } catch (error) {
    if (error instanceof TypeError && error.message.includes("circular structure")) {
      throw new TypeError(
        "The object that was given to the writeToStorage function has circular references.\nFor more information, check here: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Errors/Cyclic_object_value"
      );
    }
    throw error;
  }
}
function deleteFromStorage(key) {
  if (!utils.isBrowser()) {
    return;
  }
  globalThis.localStorage.removeItem(key);
  globalThis.dispatchEvent(
    new CustomEvent(events.LOCAL_STORAGE_CHANGE_EVENT, {
      detail: { key, value: null }
    })
  );
}
function useLocalStorage(key, initialValue) {
  const readValue = React.useCallback(() => {
    if (!utils.isBrowser()) {
      return initialValue;
    }
    try {
      if (globalThis.localStorage.hasOwnProperty(key)) {
        const item = globalThis.localStorage.getItem(key);
        if (item !== null) {
          return utils.tryParse(item);
        }
      }
    } catch (error) {
      console.warn(error);
    }
    return initialValue;
  }, [key, initialValue]);
  const [localState, setLocalState] = React.useState(readValue);
  React.useDebugValue(localState, (v) => ({ key, value: v }));
  const handleSetState = React.useCallback(
    (action) => {
      try {
        const nextState = utils.isFunction(action) ? action(localState) : action;
        writeToStorage(key, nextState);
      } catch (error) {
        console.warn(error);
      }
    },
    [key, localState]
  );
  const onStorageChange = utils.useEffectEvent((event) => {
    if (utils.isLocalStorageChanged(event)) {
      if (event.detail.key === key) {
        setLocalState(readValue());
      }
    } else {
      if (event.key === key) {
        setLocalState(readValue());
      }
    }
  });
  React.useEffect(() => {
    if (!utils.isBrowser()) {
      return;
    }
    globalThis.addEventListener("storage", onStorageChange);
    globalThis.addEventListener(events.LOCAL_STORAGE_CHANGE_EVENT, onStorageChange);
    return () => {
      globalThis.removeEventListener("storage", onStorageChange);
      globalThis.removeEventListener(events.LOCAL_STORAGE_CHANGE_EVENT, onStorageChange);
    };
  }, []);
  const removeFromStorage = React.useCallback(() => {
    deleteFromStorage(key);
  }, [key]);
  return React.useMemo(() => {
    return [localState, handleSetState, removeFromStorage];
  }, [localState, handleSetState, removeFromStorage]);
}
exports.useLocalStorage = useLocalStorage;
//# sourceMappingURL=use-local-storage.js.map
